import { useEffect } from 'react';
import { useGcsStore } from '../../store/useGcsStore';
import { useFleetVisibilityStore } from '../../store/useFleetVisibilityStore';
import { selectIsVehicleUnderMyControl } from '../../store/selectors/selectIsVehicleUnderMyControl';
import { selectVehicleStatus } from '../../store/selectors/selectVehicleStatus';
import { VehicleStatus } from '../../types/ui/vehicleStatus';
import { soundEffectsService } from '../../services/sound/soundEffectsService';
import type { GcsStore } from '../../store/useGcsStore';
import type { VehicleId } from '../../types/gcs/vehicle';
import readyForTakeoffSound from '../../assets/sounds/ready-for-takeoff.mp3';

interface GcsVehicleReadyForTakeoffListenerProps {
  gcsId: string;
}

export function GcsVehicleReadyForTakeoffListener({ gcsId }: GcsVehicleReadyForTakeoffListenerProps) {
  useEffect(() => {
    const selectVehicleStatuses = (state: GcsStore): Map<VehicleId, VehicleStatus> => {
      const statuses = new Map<VehicleId, VehicleStatus>();
      const gcsData = state.data.get(gcsId);

      if (gcsData) {
        gcsData.vehicleLaunchpadMap.forEach((_, vehicleId) => {
          const vehicleStatus = selectVehicleStatus(state.data, gcsId, vehicleId);
          if (vehicleStatus) {
            statuses.set(vehicleId, vehicleStatus);
          }
        });
      }

      return statuses;
    };

    const equalityFn = (a: Map<VehicleId, VehicleStatus>, b: Map<VehicleId, VehicleStatus>): boolean => {
      if (a.size !== b.size) return false;
      for (const [key, value] of a) {
        if (b.get(key) !== value) return false;
      }
      return true;
    };

    const unsubscribe = useGcsStore.subscribe(
      selectVehicleStatuses,
      (nextStatuses, prevStatuses = new Map()) => {
        nextStatuses.forEach((nextStatus, vehicleId) => {
          const previousStatus = prevStatuses.get(vehicleId);

          // Skip if this is the first time seeing this vehicle
          if (previousStatus === undefined) return;

          // Play sound when transitioning into a ready-for-takeoff state
          if (
            nextStatus !== previousStatus &&
            (nextStatus === VehicleStatus.READY_FOR_TAKEOFF || nextStatus === VehicleStatus.READY_FOR_TAKEOFF_FROM_DOCK)
          ) {
            const fleetVisibility = useFleetVisibilityStore.getState().visibility;
            if (
              fleetVisibility === 'mine' &&
              !selectIsVehicleUnderMyControl(useGcsStore.getState().data, gcsId, vehicleId)
            ) {
              return;
            }
            soundEffectsService.enqueue(readyForTakeoffSound);
          }
        });
      },
      {
        fireImmediately: true,
        equalityFn
      }
    );

    return () => {
      unsubscribe();
    };
  }, [gcsId]);

  return null;
}
